import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { HandlebarTemplate, ImageBadge, TextualBadge, usePromotionLayoutsContext } from '@storefront/lib-global';
import type { Maybe, ProductBadgeV2, ProductPromotion } from '@storefront/lib-global/codegen/types';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { useStaticContext } from '@storefront/lib-global/context/staticContext';
import type { BaseHitProps } from '@storefront/lib-global/globalComponents/algolia/AlgoliaHits/AlgoliaHits';
import type { ProductCarouselItemProps } from '@storefront/lib-global/globalComponents/carousel';
import { ProductItemPrice } from '@storefront/lib-global/globalComponents/carousel/productItemPrice/ProductItemPrice';
import { ImagePartial } from '@storefront/lib-global/partials/image';
import { sendSelectItemEvent } from '@storefront/lib-global/utils/gtm/events/select_item/sendSelectItemEvent';
import { sendViewProductImageEvent } from '@storefront/lib-global/utils/gtm/events/view_product_image/sendViewProductImageEvent';
import { createProductHit, createPromotionHandlebarData } from '@storefront/lib-global/utils/transformers';
import {
  ImageAndBadgeContainer,
  ImagePartialStyled,
  Price,
  ProductAndPromotionContainer,
  ProductCategory,
  ProductColour,
  ProductColourSelector,
  ProductColourSelectorSection,
  ProductItemContainer,
  ProductLink,
  ProductMoreColors,
  ProductName,
} from './ProductListItem.styles';

export type ProductListItemProps = {
  componentType: string;
  item: BaseHitProps;
  itemList?: {
    id: string;
    name?: Maybe<string> | undefined;
  };
  onClick: () => void;
  preload?: boolean;
};

type ProductSingleImageProps = ProductCarouselItemProps & {
  componentType: string;
  mainCategory: string;
  productColor?: string;
  itemList?: {
    id: string;
    name?: Maybe<string> | undefined;
  };
  badgesV2: ProductBadgeV2;
  onClick: () => void;
  preload?: boolean;
  promotion?: ProductPromotion;
};

function ProductListSingleImage({
  componentType,
  brand,
  primaryCategory,
  categoryName,
  productColor,
  imageUrls,
  productName,
  prices,
  slug,
  sku,
  colors,
  itemList,
  onClick,
  badgesV2,
  preload,
  promotion,
}: Omit<ProductSingleImageProps, 'isMultiImage' | 'productId' | 'multiImageButtonText' | 'productKey'>) {
  const { t } = useTranslation(['lib-global-common']);
  const productImage = imageUrls[0];
  const { host, baseUrl } = useStaticContext();
  const { locale } = useLocaleContext();
  const { promotionLayouts } = usePromotionLayoutsContext();

  const areAllPricesUndefined = prices.every((item) => item.actualPrice === undefined);
  const promotionHandlebarData = createPromotionHandlebarData({ promotionLayouts, promotion });

  const handleClick = (typeName: string, linkUrl: string, index: number) => () => {
    onClick();
    sendSelectItemEvent(
      {
        itemListName: itemList?.name || undefined,
        itemListId: itemList?.id || undefined,
        primaryCategoryKey: primaryCategory?.key ?? undefined,
        primaryCategoryName: primaryCategory?.name ?? undefined,
        key: sku,
        name: productName ?? '',
        brand,
        color: productColor ?? '',
        images: imageUrls,
        slug,
      },
      prices,
      baseUrl,
    );
    sendViewProductImageEvent({
      component_type: `${componentType} - ${typeName}`,
      item_name: [productName, productColor].filter(Boolean).join(' '),
      item_id: sku ?? '',
      link_url: `https://${host}/${locale}${linkUrl}`,
      index,
    });
  };

  return (
    <ProductItemContainer id={sku} data-testid="productListItem">
      <ProductAndPromotionContainer>
        <Link href={`/${slug}/${sku}-p`} passHref>
          <ProductLink
            onClick={handleClick('Product Card', `/${slug}/${sku}-p`, 0)}
            aria-label={`${t('go.to.pdp.page', { productName })}`}
          >
            <ImageAndBadgeContainer>
              <TextualBadge badge={badgesV2?.textual} />
              <ImageBadge badge={badgesV2?.image} />

              <ImagePartialStyled
                src={productImage || ''}
                width={500}
                height={500}
                data-testid="productListImage"
                aria-hidden
                lazyLoading={!preload}
              />
            </ImageAndBadgeContainer>

            {productName && (
              <ProductName title={productName} data-testid="productListName">
                {productName}
              </ProductName>
            )}
            <ProductCategory aria-hidden>
              {productColor && <p title={productColor}>{productColor}</p>}
              {categoryName && <p title={categoryName}>{categoryName}</p>}
            </ProductCategory>
            {!areAllPricesUndefined &&
              prices.map((price, i) => (
                <Price data-testid="productListPrice" aria-hidden key={i}>
                  <ProductItemPrice price={price} promotionHandlebarData={promotionHandlebarData} />
                </Price>
              ))}
          </ProductLink>
        </Link>
        {promotionHandlebarData && (
          <HandlebarTemplate template={promotionHandlebarData.template} input={promotionHandlebarData.input} />
        )}
      </ProductAndPromotionContainer>
      {colors && colors?.length > 0 && (
        <ProductColourSelectorSection>
          <ProductColourSelector>
            {colors.length > 0 &&
              colors.slice(0, 3).map((color, i) => (
                <Link key={color?.key} href={`/${slug}/${color?.key}-p`} passHref>
                  <ProductColour
                    $isSelectedColorLink={false}
                    onClick={handleClick('Product Color', `/${slug}/${color?.key}-p`, i)}
                    aria-label={`${t('go.to.pdp.page', { productName })} - ${color?.color}`}
                  >
                    <ImagePartial src={color?.image || ''} layout="fill" objectFit="contain" aria-hidden />
                  </ProductColour>
                </Link>
              ))}
            {colors && colors?.length > 3 && (
              <Link href={`/${slug}/${sku}-p`} passHref>
                <ProductMoreColors onClick={handleClick('Product Color', `/${slug}/${sku}-p`, 4)}>
                  {t('plp.more', { number: colors.length - 3 })}
                </ProductMoreColors>
              </Link>
            )}
          </ProductColourSelector>
        </ProductColourSelectorSection>
      )}
    </ProductItemContainer>
  );
}

export function ProductListItem({ componentType, item, itemList, onClick, preload }: ProductListItemProps) {
  const { locale } = useLocaleContext();
  const {
    price,
    wasPrice,
    thenPrice,
    name,
    color,
    rangeGroupDescription,
    image,
    slug,
    colors,
    objectID,
    category,
    primaryCategory,
    badgesV2,
    styleName,
    promotion,
    ...rest
  } = createProductHit({
    item,
    locale,
  });
  const priceObject = [{ actualPrice: price, wasPrice, thenPrice }];

  return (
    <ProductListSingleImage
      primaryCategory={primaryCategory}
      categoryName={rangeGroupDescription}
      componentType={componentType}
      colors={colors}
      imageUrls={image}
      productColor={color}
      productName={styleName || name}
      prices={priceObject}
      sku={objectID}
      slug={slug}
      onClick={onClick}
      mainCategory={category}
      itemList={itemList}
      badgesV2={badgesV2}
      preload={preload}
      promotion={promotion}
      {...rest}
    />
  );
}

export default ProductListItem;
